import open from "open";

import { OneGrepApiClient, OneGrepApiHighLevelClient } from "@toolprint/sdk";
import { ConfigProvider } from "../config/provider.js";
import {
  apiKeyClientFromConfig,
  apiKeyFromConfig,
  bearerAuthClientFromConfig,
} from "../api.js";
import { PropelAuthProvider, PropelAuthUserJwtClaims } from "./propel.js";
import { logger } from "../logger.js";
import { TokensResponse } from "./oauth.js";

// ! TODO: Isolated API factory methods and rename better
export class AuthProvider {
  private readonly configProvider: ConfigProvider;
  private readonly propelAuthProvider: PropelAuthProvider;

  constructor(params: { configProvider: ConfigProvider }) {
    this.configProvider = params.configProvider;
    this.propelAuthProvider = new PropelAuthProvider({
      configProvider: this.configProvider,
    });
    logger.debug("AuthzProvider initialized");
  }

  async getBearerAuthApi(): Promise<OneGrepApiHighLevelClient> {
    const config = await this.configProvider.getConfig();
    return bearerAuthClientFromConfig(config);
  }

  async getApiKey(): Promise<string> {
    const config = await this.configProvider.getConfig();
    return apiKeyFromConfig(config);
  }

  async getApiKeyClient(): Promise<OneGrepApiClient> {
    const config = await this.configProvider.getConfig();
    return apiKeyClientFromConfig(config);
  }

  async getApi(): Promise<OneGrepApiHighLevelClient> {
    return new OneGrepApiHighLevelClient(await this.getApiKeyClient());
  }

  async isUserIdentified(): Promise<boolean> {
    const currentConfig = await this.configProvider.getConfig();
    return !!currentConfig.identity;
  }

  async isAuthenticated(): Promise<boolean> {
    try {
      const api = await this.getApi();
      const response = await api.authStatus();
      return response.credentials_provided && response.is_authenticated;
    } catch (error) {
      logger.error(`Error checking authentication status: ${error}`);
      return false;
    }
  }

  async signup(
    invitationCode: string,
    loginMode: "callback" | "mcp-gateway" = "callback",
  ): Promise<void> {
    await this.login(loginMode);
    logger.debug("Sign-Up Login successful");

    const config = await this.configProvider.getConfig();
    const userEmail = config.identity?.user?.email;
    if (!userEmail) {
      throw new Error("User email not found in config");
    }

    const client = bearerAuthClientFromConfig(config);
    logger.debug(`Connected to API endpoint: ${config.api?.url}`);

    try {
      const authStatus = await client.authStatus();
      logger.info(`Auth status: ${JSON.stringify(authStatus)}`);
      if (authStatus.user_id) {
        logger.debug("User already joined organization");
        return;
      }
    } catch (error) {
      logger.error(`Error checking if user is already joined: ${error}`);
    }

    // Create an account for this user in the domain that they're pointed at.
    logger.debug(
      `Creating account with email: ${userEmail} and invitation code: ${invitationCode}`,
    );
    try {
      const accountInfo = await client.createAccountByInvitation(
        invitationCode,
        userEmail,
      );
      logger.debug(
        `User account created and joined organization: ${accountInfo.organization.id}`,
      );
    } catch (error) {
      logger.error(`Error creating account: ${error}`);
      // TODO: Catch HTTP 409 errors and throw a more specific error message in that case.
      throw new Error(
        "Failed to create account. Check if you already created an account with this email address.",
      );
    }
  }

  async login(
    loginMode: "callback" | "mcp-gateway" = "callback",
    output: Console = console,
  ): Promise<void> {
    if (loginMode === "callback") {
      const { tokens, userDetails } =
        await this.propelAuthProvider.loginWithOAuthCallback(output);
      logger.debug(
        `PropelAuth login successful: ${JSON.stringify(userDetails)}`,
      );

      // Update the config store with the new account info.
      await this.updateConfigStore(tokens, userDetails);
    } else if (loginMode === "mcp-gateway") {
      await this.openAccountPage();
      // TODO: Wait for the user to login and then update the config store.
    } else {
      throw new Error(`Invalid login mode: ${loginMode}`);
    }
  }

  /**
   * Update the config store with the provided tokens and JWT claims.
   */
  private async updateConfigStore(
    tokens: TokensResponse,
    userDetails: PropelAuthUserJwtClaims,
  ) {
    await this.configProvider.update({
      credentials: {
        accessToken: tokens.access_token,
      },
      identity: {
        user: {
          id: userDetails.user_id,
          email: userDetails.email,
        },
      },
    });
    logger.debug(`Config store identity and credentials updated.`);
  }

  /**
   * Logout the user by removing the credentials and identity from the config store.
   *
   * TODO: Call propel auth logout endpoint?
   */
  async logout(): Promise<void> {
    await this.propelAuthProvider.logout();
    logger.debug("PropelAuth logout successful");

    await this.configProvider.update({
      credentials: {
        accessToken: undefined,
      },
      identity: undefined,
    });
    logger.debug(`Config store identity and credentials removed.`);
  }

  async openSignupPage(): Promise<void> {
    const url = await this.propelAuthProvider.signupUrl();
    await open(url.toString());
  }

  async openLoginPage(): Promise<void> {
    const url = await this.propelAuthProvider.loginUrl();
    await open(url.toString());
  }

  async openAccountPage(): Promise<void> {
    const url = await this.propelAuthProvider.accountUrl();
    await open(url.toString());
  }
}

export default AuthProvider;
